import { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import MailIcon from '@material-ui/icons/Mail';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { getAdditionalUserData, mailResults } from '../../../services/Student';
import { ContentPaper, ContentHeader } from '../styled';
import ChartPanel from './ChartPanel';
import Popup from '../../Popup';
import { getHelpUrl } from '../../../services/shared';
import { useAuthContext } from '../../../contexts/AuthContext';

const MainContainer = styled.div`
    display: flex;
    gap: 16px;
`;

const TablePaper = styled(ContentPaper)`
    flex: 0 1 540px;
    @media (max-width: 1200px) {
        flex: 0 1 480px;
    }
`;

const TableContainer = styled.div`
    display: inline-block;
    width: 100%;
    font-size: 90%;
    @media (max-width: 1200px) {
        font-size: 80%;
    }
`;

const StyledTable = styled.table`
    border-collapse: collapse;
    width: 100%;

    & td,
    & th {
        border: 1px solid #999;
        padding: 2px 8px;
    }

    & th {
        text-align: left;
    }
`;

const ChartsContainer = styled.div`
    vertical-align: top;
    flex: 1;
    display: inline-flex;
    flex-direction: column;
    gap: 16px;
`;

const ChartPanelFullScreen = styled(ChartPanel).attrs({ chartHeight: '100%' })`
    width: 96vw;
    height: 48vw;
    max-height: 96vh;
`;

const Result = ({ userToken }) => {
    const { t } = useTranslation();
    const { auth } = useAuthContext();
    const [userResults, setUserResults] = useState();
    const [winnerResults, setWinnerResults] = useState();
    const [charts, setCharts] = useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const [chartPopupOpen, setChartPopupOpen] = useState(false);

    const username = userResults?.name;
    const winnername = winnerResults?.name;
    useEffect(() => {
        let mounted = true;
        getAdditionalUserData(userToken).then((responseData) => {
            if (!mounted) return;

            setUserResults({ ...responseData.player });
            setWinnerResults({ ...responseData.winner });
            setCharts(responseData.charts || []);
        });

        return () => {
            mounted = false;
        };
    }, [userToken]);

    const resultTableData = userResults?.results
        ? Object.entries(userResults.results)
              //   .filter(([key, value]) => key !== 'harvest')
              .map(([key, value]) => {
                  return {
                      header: key,
                      data: Object.entries(value).map(([valkey, valvalue]) => {
                          const winnerValue =
                              winnerResults?.results?.[key]?.[valkey];

                          return [`${valkey}`, valvalue, winnerValue];
                      }),
                  };
              })
        : [];

    const parameterData = userResults?.parameters
        ? Object.entries(userResults.parameters)
              .filter(([key]) => key === 'preset')
              .map(([key, value]) => {
                  return {
                      header: t('gameResults.Fixed parameters'),
                      data: Object.entries(value).map(([valkey, valvalue]) => {
                          const winnerValue =
                              winnerResults?.parameters?.[key]?.[valkey];

                          return [valkey, valvalue, winnerValue];
                      }),
                  };
              })
        : [];

    const handleMailResult = () => {
        mailResults(userToken)
            .then((r) => {
                enqueueSnackbar(t('mailResultsSent'), {
                    variant: 'success',
                });
            })
            .catch((error) => {
                enqueueSnackbar(
                    `${t('error.mailResultsFailed')} (${error.message})`,
                    {
                        variant: 'error',
                    }
                );
            });
    };

    return (
        <MainContainer>
            <TablePaper>
                <ContentHeader>{t('game.Results')}</ContentHeader>
                <p>{t('game.GameFinishedExplanation')}</p>
                <p>
                    <Button
                        onClick={handleMailResult}
                        variant="contained"
                        color="primary"
                        startIcon={<MailIcon />}
                    >
                        {t('buttons.mailResults')}
                    </Button>
                </p>
                <TableContainer>
                    <StyledTable>
                        <thead>
                            <tr>
                                <th></th>
                                <th>
                                    {t('gameResults.you')} ({username})
                                </th>
                                <th>
                                    {t('gameResults.winner')} ({winnername})
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...resultTableData, ...parameterData].map(
                                (group) => {
                                    return [
                                        <tr key={group.header}>
                                            <th colSpan="3">{group.header}</th>
                                        </tr>,
                                        ...group.data.map((rowData, i) => {
                                            return (
                                                <tr key={i}>
                                                    <td>{rowData[0]}</td>
                                                    <td>{rowData[1]}</td>
                                                    <td>{rowData[2]}</td>
                                                </tr>
                                            );
                                        }),
                                    ];
                                }
                            )}
                        </tbody>
                    </StyledTable>
                </TableContainer>
            </TablePaper>
            <ChartsContainer>
                {charts.map((chart) => {
                    return (
                        <ChartPanel
                            key={chart.title}
                            title={chart.title}
                            data={chart}
                            openPopup={() => setChartPopupOpen(chart)}
                            helpUrl={getHelpUrl(
                                chart.url,
                                auth.gameConfig,
                                auth.languageId
                            )}
                        />
                    );
                })}
            </ChartsContainer>
            <Popup
                open={!!chartPopupOpen}
                onClose={() => {
                    setChartPopupOpen(null);
                }}
            >
                {chartPopupOpen && (
                    <ChartPanelFullScreen
                        title={chartPopupOpen.title}
                        data={chartPopupOpen}
                        helpUrl={getHelpUrl(
                            chartPopupOpen.url,
                            auth.gameConfig,
                            auth.languageId
                        )}
                    />
                )}
            </Popup>
        </MainContainer>
    );
};

export default Result;
